import { memo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useFindingStore, Finding } from "stores/FindingStore";
import { useIgnoreStore } from "stores/IgnoreStore";

import { notifyError, notifySuccess } from "utility/notify";
import { FOTS } from "utility/typescript";

import SelectedFindings from "./SelectedFindings";

// -----------------------------------------------------------------------------

const BulkIgnoreDialog = memo(function BulkIgnoreDialog({
  findings,
  onClose,
}: BulkIgnoreDialogProps): JSX.Element {
  const [, ignoreActions] = useIgnoreStore();
  const [, findingActions] = useFindingStore();

  const [ignoreReason, setIgnoreReason] = useState("");
  const [loading, setLoading] = useState(false);

  const onIgnore = () => {
    setLoading(true);

    const data = {
      uuids: findings.map((finding) => finding.uuid),
      ignoreReason,
      onSuccess,
      onError,
    };

    ignoreActions.bulkIgnoreFinding(data);
  };

  const onSuccess = () => {
    findingActions.resetDeeplinkFindings();
    notifySuccess(`Ignored ${findings.length} findings`);
    setLoading(false);
    onClose();
  };

  const onError = (errorCount: number) => {
    if (errorCount === findings.length) {
      notifyError("Something went wrong ignoring the findings");
    } else {
      notifyError("Something went wrong ignoring some of the findings");
    }
    setLoading(false);
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth>
      <DialogTitle>Ignore Findings</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Would you like to ignore instances of these {findings.length} selected
          findings:
        </Typography>
        <SelectedFindings findings={findings} />

        <Typography sx={{ mt: 3, mb: 2 }}>Reason: (optional)</Typography>
        <TextField
          id="ignore-reason-text-field"
          placeholder="Describe why you are ignoring these findings"
          variant="outlined"
          multiline
          maxRows={4}
          fullWidth
          value={ignoreReason}
          onChange={(e: FOTS) => setIgnoreReason(e?.target?.value)}
        />
      </DialogContent>

      <DialogActions sx={{ pt: 0, pb: 3, px: 3 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton loading={loading} variant="contained" onClick={onIgnore}>
          Ignore
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

export default BulkIgnoreDialog;

// -----------------------------------------------------------------------------

interface BulkIgnoreDialogProps {
  findings: Finding[];
  onClose: () => void;
}
